/**
 * Authentication Context
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  dashboardAccess: string[];
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: { email: string; password: string; firstName: string; lastName: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    async function checkAuth() {
      const token = api.getAccessToken();
      if (token) {
        const result = await api.getMe();
        if (result.success && result.data) {
          setUser(result.data);
        }
      }
      setIsLoading(false);
    }
    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password);

    if (result.success && result.data) {
      api.setTokens(result.data.tokens.accessToken, result.data.tokens.refreshToken);
      setUser(result.data.user);
      return { success: true };
    }

    return {
      success: false,
      error: result.error?.message || 'Login failed',
    };
  }, []);

  const register = useCallback(async (data: { email: string; password: string; firstName: string; lastName: string }) => {
    const result = await api.register(data);

    if (result.success && result.data) {
      api.setTokens(result.data.tokens.accessToken, result.data.tokens.refreshToken);
      setUser(result.data.user);
      return { success: true };
    }

    return {
      success: false,
      error: result.error?.message || 'Registration failed',
    };
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
